import { orderTypeIds } from "./orderTypeIds";
import { protocolStateIds } from "./protocolStateIds";

/**
 * Seed справочника /api/v1/protocolType:
 * 1 — Производственный контроль (уходит в eSen),
 * 2 — Другое (не уходит в eSen).
 * На бэке TypeId сохраняется как ComplianceStatus.
 */
export const PROTOCOL_TYPE_IDS = {
  PRODUCTION_CONTROL: 1,
  OTHER: 2,
};

export function normalizeProtocolTypeId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function isPaidProtocolOrderType(orderType) {
  return Number(orderType) === orderTypeIds.payed;
}

/**
 * GET /protocol/{id} (DetailedProtocol) часто не отдаёт orderType,
 * в отличие от строки реестра. Восстанавливаем по связям.
 */
export function resolveProtocolOrderType(protocol, fallback) {
  const direct = Number(
    protocol?.orderType ?? protocol?.OrderType ?? fallback
  );
  if (Number.isFinite(direct) && direct > 0) return direct;

  if (protocol?.objectReferralId || protocol?.ObjectReferralId) {
    return orderTypeIds.government;
  }

  const nestedAct = protocol?.act ?? protocol?.Act;
  const actOrder = Number(nestedAct?.orderType ?? nestedAct?.OrderType);
  if (Number.isFinite(actOrder) && actOrder > 0) return actOrder;

  if (protocol?.referralId || protocol?.ReferralId) {
    return orderTypeIds.payed;
  }

  return null;
}

/**
 * Поле «Тип протокола» — только платные (OrderType = Payed).
 * Показываем в статусе «Создан» или если тип уже выбран (для 2+ подписанта).
 */
export function shouldShowProtocolType({ orderType, stateId, typeId } = {}) {
  if (!isPaidProtocolOrderType(orderType)) return false;
  if (normalizeProtocolTypeId(typeId) != null) return true;
  return Number(stateId) === protocolStateIds.created;
}

export function canEditProtocolType({
  orderType,
  stateId,
  signed,
  hasAnySignerSigned,
} = {}) {
  return (
    isPaidProtocolOrderType(orderType) &&
    Number(stateId) === protocolStateIds.created &&
    !signed &&
    !hasAnySignerSigned
  );
}

export function getProtocolTypeLabel(typeId, protocolTypes = []) {
  const id = normalizeProtocolTypeId(typeId);
  if (id == null) return "";
  const found = (protocolTypes || []).find((item) => Number(item.id) === id);
  if (found?.name) return found.name;
  if (id === PROTOCOL_TYPE_IDS.PRODUCTION_CONTROL) return "Производственный контроль";
  if (id === PROTOCOL_TYPE_IDS.OTHER) return "Другое";
  return String(id);
}

export function getProtocolTypeHelperText(typeId) {
  const id = normalizeProtocolTypeId(typeId);
  if (id === PROTOCOL_TYPE_IDS.OTHER) {
    return "Протокол в ИС СЭН (eSen) не отправляется";
  }
  if (id === PROTOCOL_TYPE_IDS.PRODUCTION_CONTROL) {
    return "Протокол будет передан в ИС СЭН (eSen)";
  }
  return "Обязательно перед подписанием (выбирает первый подписант)";
}

/** Достаём typeId из DTO; для платных бэк может отдать значение в ComplianceStatus. */
export function resolveProtocolTypeId(protocol, { orderType } = {}) {
  if (!protocol) return null;
  const fromTypeId =
    protocol.typeId ??
    protocol.TypeId ??
    protocol.type?.id ??
    protocol.Type?.id;
  const normalized = normalizeProtocolTypeId(fromTypeId);
  if (normalized != null) return normalized;

  const effectiveOrderType =
    orderType ?? protocol.orderType ?? protocol.OrderType;
  if (!isPaidProtocolOrderType(effectiveOrderType)) return null;

  return normalizeProtocolTypeId(
    protocol.complianceStatus ?? protocol.ComplianceStatus
  );
}

export function withProtocolTypeContext(protocol, fallbackOrderType) {
  if (!protocol) return protocol;
  const orderType = resolveProtocolOrderType(protocol, fallbackOrderType);
  const typeId = resolveProtocolTypeId(protocol, { orderType });
  return {
    ...protocol,
    ...(orderType != null ? { orderType } : {}),
    ...(typeId != null ? { typeId } : {}),
  };
}
